/* ───────────────────────────────────────────────────────────
   DepartmentSetup.jsx – CRUD grid for /api/departments
   ─────────────────────────────────────────────────────────── */
   import React, { useState, useEffect, useCallback } from "react";
   import API_BASE_URL from "../../../../../../Constants";
   
   import {
     Box, Table, TableHead, TableBody, TableRow, TableCell,
     IconButton, TextField, MenuItem, Tooltip,
     Snackbar, Alert, Drawer
   } from "@mui/material";
   
   import ModeIcon    from "@mui/icons-material/ModeOutlined";
   import SaveIcon    from "@mui/icons-material/SaveOutlined";
   import AddIcon     from "@mui/icons-material/AddOutlined";
   import RefreshIcon from "@mui/icons-material/RefreshOutlined";
   import CloseIcon   from "@mui/icons-material/CloseOutlined";
   import DeleteIcon  from "@mui/icons-material/DeleteOutlineOutlined";
   
   /* ─── constants / helpers ────────────────────────────────── */
   const ENDPOINT = `${API_BASE_URL}/api/departments`;
   const WEEK_STARTS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
   const uid = (()=>{let n=0;return()=>`tmp-${++n}`})();  // tmp-ids
   
   const trimBlanks = obj =>
     Object.fromEntries(Object.entries(obj).map(([k,v])=>[k,v===""?null:v]));
   
   const jsonFetch = (url, opts={}) =>
     fetch(url,{
       credentials:"include",
       headers:{ "Content-Type":"application/json" },
       ...opts,
       body: opts.body ? JSON.stringify(opts.body) : undefined
     }).then(async r=>{
       if(!r.ok) throw new Error(await r.text()||r.statusText);
       return r.status===204 ? null : r.json();
     });
   
   /* ─── component ─────────────────────────────────────────── */
   export default function DepartmentSetup({ companyId=null, onClose }) {
   
     const [rows,setRows]     = useState([]);
     const [deleted,setDel]   = useState([]);
     const [dirty,setDirty]   = useState(false);
     const [saving,setSave]   = useState(false);
     const [msg,setMsg]       = useState(null);
     const [isEditing,setEdit]= useState(false);
     const [loading,setLoad]  = useState(false);
   
     /* ------ load -------------------------------------------------------- */
     const load = useCallback(async()=>{
       try{
         setLoad(true);
         const url = companyId ? `${ENDPOINT}/company/${companyId}` : ENDPOINT;
         const data = await jsonFetch(url);
         setRows((data||[]).map(r=>({...r,__dirty:false})));
         setDel([]); setDirty(false); setEdit(false);
       }catch(e){
         setMsg({severity:"error",text:`Load failed: ${e.message}`});
       }finally{ setLoad(false); }
     },[companyId]);
   
     useEffect(()=>{ load(); },[load]);
   
     /* ------ grid helpers ------------------------------------------------ */
     const markDirty=()=>!dirty&&setDirty(true);
   
     const updateField = (idx,field)=>e=>{
       const value=e.target.value;
       setRows(p=>p.map((r,i)=>i===idx?{...r,[field]:value,__dirty:true}:r));
       markDirty();
     };
   
     const addRow = ()=>{
       setRows(p=>[
         {
           id:uid(), companyId,
           name:"", code:"", poPrefix:"", invoicePrefix:"",
           weekStarts:"Sunday", otEndAm:"", otStartPm:"",
           defaultDayHours:"", defaultWeekHrs:"", managerId:"",
           __dirty:true
         },
         ...p
       ]);
       setEdit(true); markDirty();
     };
   
     const deleteRow = idx=>()=> {
       setRows(p=>{
         const copy=[...p]; const [v]=copy.splice(idx,1);
         if(v?.id && !String(v.id).startsWith("tmp")) setDel(d=>[...d,v.id]);
         return copy;
       });
       markDirty();
     };
   
     /* ------ persist ----------------------------------------------------- */
     const persist = async()=>{
       if(saving) return;
       setSave(true);
       try{
         /* delete */
         await Promise.all(deleted.map(id=>jsonFetch(`${ENDPOINT}/${id}`,{method:"DELETE"})));
   
         /* insert / update */
         const work = rows.flatMap(r=>{
           const body = trimBlanks({...r,companyId});
           if(String(r.id).startsWith("tmp")){
             delete body.id;
             return jsonFetch(ENDPOINT,{method:"POST",body});
           }
           if(r.__dirty){
             return jsonFetch(`${ENDPOINT}/${r.id}`,{method:"PUT",body});
           }
           return [];
         });
         await Promise.all(work);
   
         await load();
         setMsg({severity:"success",text:`Saved ${work.length+deleted.length} change(s)`});
       }catch(e){
         setMsg({severity:"error",text:`Save failed: ${e.message}`});
       }finally{ setSave(false); }
     };
   
     /* ------ cell renderer ------------------------------------------------ */
     const viewCell = (v,w)=>
       <div className="bg-gray-50 px-2 py-1 rounded text-sm" style={{minWidth:w}}>
         {v||<span className="text-gray-400">—</span>}
       </div>;
   
     const renderCell = (idx,f,w=110,opts=null)=>{
       if(!isEditing) return viewCell(rows[idx][f],w);
   
       if(f==="otEndAm"||f==="otStartPm"){
         return <TextField variant="standard" type="time"
                 value={rows[idx][f]??""} onChange={updateField(idx,f)}
                 inputProps={{step:900}} sx={{width:w}}/>;
       }
       if(opts){
         return (
           <TextField select variant="standard"
             value={rows[idx][f]??""} onChange={updateField(idx,f)}
             sx={{width:w}}>
             {opts.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}
           </TextField>
         );
       }
       return <TextField variant="standard"
                value={rows[idx][f]??""} onChange={updateField(idx,f)}
                sx={{width:w}}/>;
     };
   
     const HEAD=["Department","Code","PO","Inv","Week Starts","OT End AM",
                 "OT Start PM","Day Hrs","Wk Hrs","Manager",""];
   
     /* ------ UI ---------------------------------------------------------- */
     return (
       <Box p={2} sx={{display:"flex",gap:2,overflowX:"auto"}}>
         <Box sx={{flex:2,minWidth:720}}>
           {/* toolbar */}
           <Box mb={1} display="flex" gap={2}>
             {/* edit / save */}
             <Tooltip title={isEditing?"Save changes":"Edit"}><span>
               <IconButton size="small" disabled={saving}
                 onClick={async()=>{ isEditing ? await persist() : setEdit(true); }}>
                 {isEditing
                   ? <SaveIcon fontSize="small" className={saving?"text-gray-400":"text-blue-600"}/>
                   : <ModeIcon fontSize="small" className="text-gray-700"/>}
               </IconButton>
             </span></Tooltip>
   
             {/* add */}
             <Tooltip title="Add row"><span>
               <IconButton size="small" disabled={saving} onClick={addRow}>
                 <AddIcon fontSize="small"/>
               </IconButton>
             </span></Tooltip>
   
             {/* refresh */}
             <Tooltip title="Reload list"><span>
               <IconButton size="small" disabled={saving||loading} onClick={load}>
                 <RefreshIcon fontSize="small"/>
               </IconButton>
             </span></Tooltip>
   
             {/* close */}
             {onClose&&(
               <Tooltip title="Close"><span>
                 <IconButton size="small" onClick={onClose}>
                   <CloseIcon fontSize="small"/>
                 </IconButton>
               </span></Tooltip>
             )}
             {saving && <span className="text-xs text-gray-500 ml-2">Saving…</span>}
           </Box>
   
           {/* table */}
           <Table size="small">
             <TableHead>
               <TableRow>{HEAD.map(h=><TableCell key={h} sx={{fontWeight:600}}>{h}</TableCell>)}</TableRow>
             </TableHead>
             <TableBody>
               {rows.map((r,i)=>(
                 <TableRow key={r.id} hover>
                   <TableCell>{renderCell(i,"name",160)}</TableCell>
                   <TableCell>{renderCell(i,"code",70)}</TableCell>
                   <TableCell>{renderCell(i,"poPrefix",70)}</TableCell>
                   <TableCell>{renderCell(i,"invoicePrefix",70)}</TableCell>
                   <TableCell>{renderCell(i,"weekStarts",120,WEEK_STARTS)}</TableCell>
                   <TableCell>{renderCell(i,"otEndAm",110)}</TableCell>
                   <TableCell>{renderCell(i,"otStartPm",110)}</TableCell>
                   <TableCell>{renderCell(i,"defaultDayHours",60)}</TableCell>
                   <TableCell>{renderCell(i,"defaultWeekHrs",60)}</TableCell>
                   <TableCell>{renderCell(i,"managerId",100)}</TableCell>
                   <TableCell>
                     <Tooltip title="Delete row"><span>
                       <IconButton size="small" disabled={!isEditing} onClick={deleteRow(i)}>
                         <DeleteIcon fontSize="inherit"/>
                       </IconButton>
                     </span></Tooltip>
                   </TableCell>
                 </TableRow>
               ))}
             </TableBody>
           </Table>
         </Box>
   
         {/* placeholder drawer */}
         <Drawer anchor="right" open={false}/>
   
         {/* snackbar */}
         <Snackbar open={!!msg} autoHideDuration={4000}
                   onClose={()=>setMsg(null)}
                   anchorOrigin={{vertical:"bottom",horizontal:"center"}}>
           {msg && <Alert severity={msg.severity} variant="filled">{msg.text}</Alert>}
         </Snackbar>
       </Box>
     );
   }
   